import type { Router } from 'vue-router';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';

import { SnowplowContext, SnowplowSraContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { Spectre } from '@/libraries/tracking';
import { useSessionManager } from '@/libraries/tracking/nuxt';
import { getStorageItem, removeStorageItem, setStorageItem } from '@/helpers/storage-helper';
import { decodeRoutePath } from '@/helpers/route-path-helper';

import { getJWTVShortNames, isJWTVOffer } from '@/features/jwtv/config';

const { defineSpec, createSpecTracker } = Spectre<SnowplowContext>(() => TrackingHelper.trackEvent);
const specs = {
	// Age gate impression (non-interaction)
	ageGateImpression: {
		category: 'age_gate',
		action: 'impression',
		nonInteraction: true,
	},

	// Age verification success
	ageGateSuccess: {
		category: 'age_gate',
		action: 'success',
	},

	// Age verification failure
	ageGateFailure: {
		category: 'age_gate',
		action: 'failure',
	},

	// User closes age gate without completing
	ageGateClose: {
		category: 'age_gate',
		action: 'close',
	},

	// Failed age check screen impression (non-interaction)
	returnToHomeImpression: {
		category: 'age_gate',
		action: 'return_to_home_impression',
		nonInteraction: true,
	},

	// Return to home button clicked
	returnToHomeClicked: {
		category: 'age_gate',
		action: 'return_to_home_clicked',
	},

	// Similar titles carousel loaded
	similarTitlesLoaded: {
		category: 'similartitles',
		action: 'loaded',
		label: '-18 similar titles',
		property: 'age_gate',
		nonInteraction: true,
	},

	// Title impression in carousel
	titleImpression: defineSpec<{
		// Could include title ID if needed
	}>()({
		category: 'title',
		action: 'impression',
		property: 'age_gate',
		nonInteraction: true,
	}),

	// Similar title clicked
	similarTitleClicked: defineSpec<{
		label: string; // Format: "base_title_id,clicked_title_id"
		value: number; // 0-indexed position
	}>()({
		category: 'userinteraction',
		action: 'title_clicked',
		property: 'age_gate',
	}),

	errorImpression: defineSpec<{
		label: 'geo_restricted' | 'generic_error' | 'age_restricted';
	}>()({
		category: 'jwtv_avod_error',
		action: 'impression',
		nonInteraction: true,
	}),

	// Non-SAVOD banner impression (non-interaction)
	nonSavodBannerImpression: {
		category: 'jwtv_signup_banner',
		action: 'impression',
		property: 'jwtv_signup_banner',
		nonInteraction: true,
	},

	// Non-SAVOD banner CTA clicked
	nonSavodBannerClicked: {
		category: 'jwtv_signup_banner',
		action: 'cta_clicked',
		property: 'jwtv_signup_banner',
	},
};

export function useJwtvAvodTracking() {
	const trackJwtvAvod = createSpecTracker({ specs });

	return { trackJwtvAvod };
}

/* SR SESSION */
type SrContextPayload = ConstructorParameters<typeof SnowplowSraContext>[0];
type SrSessionStart = { titlePath: string };
type SrSessionHandoff = { sraContext: SrContextPayload; titlePath: string; expiresAt: number };

// Official JWTV providers only, partial ones just reuse the JWTV player
const JWTV_SHORT_NAMES = getJWTVShortNames();

const SR_SESSION_HANDOFF_KEY = 'sr_session_handoff';
// Long enough for the new tab of a poster click-out to load
const SR_SESSION_HANDOFF_TTL_MS = 60 * 1000;

// Fallback only, so a forgotten tab doesn't carry the context past the campaign
const SR_SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000;

const { session, attachSession, killSession, startInterval } = useSessionManager<SnowplowSraContext, SrSessionStart>({
	id: Symbol('SrSession'),
	influenced: true,
	sessionTimeoutMs: SR_SESSION_TIMEOUT_MS,
});

/** `/us/tv-show/show-name[/season-X]` → `/us/tv-show/show-name`, so a show and its seasons are the same title. */
function getTitleBasePath(path: string) {
	// List SRs can link their titles with full URLs, window.location paths are percent-encoded
	const pathname = /^https?:\/\//.test(path) ? new URL(path).pathname : path;
	return decodeRoutePath(pathname).split('/').slice(0, 4).join('/');
}

// The clicked SR, its session starts once this tab navigates to its title
let pendingSrSession: { srContext: SnowplowSraContext; titlePath: string } | null = null;

/** The SR's `sra_context`, only for SRs whose watch-now offer is an official JWTV offer. */
export function getSrContext(sponsoredAd: SponsoredAdFragment | null | undefined) {
	const sraContext = sponsoredAd?.sraContext;
	const offer = sponsoredAd?.campaign?.watchNowOffer;
	if (sraContext?.bidId == null || offer == null) return null;

	const isJwtvOffer = JWTV_SHORT_NAMES.includes(offer.package.shortName) && isJWTVOffer(offer);

	return isJwtvOffer ? new SnowplowSraContext(sraContext) : null;
}

/** Adds the SR's `sra_context` to all events on its title and replaces any running SR session. */
export function startSrSession(srContext: SnowplowSraContext | null, titlePath: string) {
	if (srContext == null) return;

	const start = { titlePath: getTitleBasePath(titlePath) };
	// If there's a match with the current path, attach the session
	if (getTitleBasePath(window.location.pathname) === start.titlePath) attachSession(start, srContext);
	else pendingSrSession = { srContext, ...start }; //otherwise till user goes to that new tab
}

/**
 * Ends the JWTV influenced SR session, so later events drop its `sra_context`. Called when the SR's JWTV player closes.
 * Leaving the title ends it in `setupSrSession`, closing the tab ends it as it only lives in memory.
 */
export function killSrSession() {
	killSession();
}

/** Poster click-outs open the title in a new tab, which picks the session up in `setupSrSession`. */
export function handOffSrSession(srContext: SnowplowSraContext | null, titlePath: string) {
	if (srContext == null) return;

	const { bidId, bidRequest, bidResponse } = srContext;
	setStorageItem<SrSessionHandoff>(SR_SESSION_HANDOFF_KEY, {
		sraContext: { bidId, bidRequest, bidResponse },
		titlePath: getTitleBasePath(titlePath),
		expiresAt: Date.now() + SR_SESSION_HANDOFF_TTL_MS,
	});
}

/** Client only: picks up a handed off session and ends the session on leaving its title or after the fallback timeout. */
export function setupSrSession(router: Router) {
	// Started here as it needs the Nuxt context, it no-ops while there's no session
	startInterval();

	// Picked up right away, as the first page view can fire before the router is ready
	const handoff = getStorageItem<SrSessionHandoff>(SR_SESSION_HANDOFF_KEY);
	if (handoff != null) {
		removeStorageItem(SR_SESSION_HANDOFF_KEY);

		const initialPath = router.resolve(window.location.pathname).path;
		if (handoff.expiresAt > Date.now() && getTitleBasePath(initialPath) === handoff.titlePath) {
			startSrSession(new SnowplowSraContext(handoff.sraContext), handoff.titlePath);
		}
	}

	// End SR session
	router.beforeEach(to => {
		if (pendingSrSession?.titlePath === getTitleBasePath(to.path)) {
			attachSession({ titlePath: pendingSrSession.titlePath }, pendingSrSession.srContext);
			// This tab opened the title, so a new tab must not pick the same session up
			removeStorageItem(SR_SESSION_HANDOFF_KEY);
		}
		pendingSrSession = null;

		const currentSession = session();
		if (currentSession == null) return;

		if (getTitleBasePath(to.path) !== currentSession.start.titlePath) killSession();
	});
}
